import {
  metricPath,
  metricValue,
  type FleetMetricEnvelope,
} from "../../api/mcp";
import { Bars } from "../../components/charts";
import { ComingSoon } from "../../components/ComingSoon";
import {
  CouldNotLook,
  Denied,
  LoadFailed,
  Loading,
  SignedOut,
} from "../../components/states";
import { Card, Notice } from "../../components/ui";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";
import { ProductTag } from "../../products/PerProduct";

/**
 * Labeeb's own surfaces — what is left of them.
 *
 * This file used to hold the agent-grant pages: `BetaGrants`, its
 * `TenantGrantPanel` lookup, a configured-tenant list and a usage panel, all
 * built on `PATCH /admin/tenant/{id}` with `allowed_agents` / `beta_agents`.
 * Labeeb's new admin API has none of that — the route is gone and the word
 * "agent" does not appear once in its OpenAPI spec. Per-merchant capability
 * is now `enabled_domains` on the merchant page, and the tier is its own
 * quota document, so nothing here needed porting: the pages had no API left
 * and `/policy/beta` already redirected to `/tenants`.
 *
 * What survives is fleet evidence, which was never about agents.
 */

/**
 * The two MCP metrics this page can actually show, live — the ones the
 * design's blurred evidence panel below asks for that happen to already
 * exist: which tools the fleet is spending credits on, and how many tools
 * on each tier have their own cap row rather than inheriting `*`. Neither is
 * Snowflake-shaped (no histograms, no cost months, no cohorts), but both are
 * real, unread anywhere else, and belong on a page whose whole subject is
 * fleet-wide evidence.
 *
 * A SEPARATE component from `ComingSoon`, deliberately: `ComingSoon` is
 * asserted (ComingSoon.test.ts) to never fetch, so nothing invented can ever
 * reach it. This panel fetches on purpose, and sits below the blurred
 * Labeeb/Snowflake shape rather than inside it — real evidence, next to an
 * honest gap, never blended into one.
 */
function McpEvidence({ config }: { config: AppConfig }) {
  const topTools = useResource<FleetMetricEnvelope<"top_tools_today">>(
    config,
    metricPath("top_tools_today"),
  );
  const overrides = useResource<FleetMetricEnvelope<"quota_overrides_count">>(
    config,
    metricPath("quota_overrides_count"),
  );

  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      <Card
        title="Top tools today"
        note="By credits spent · UTC day · MCP, not Snowflake"
        aside={<ProductTag id="mcp" />}
      >
        {topTools.state.kind === "loading" && <Loading what="top tools" />}
        {topTools.state.kind === "signedout" && <SignedOut />}
        {topTools.state.kind === "denied" && <Denied message={topTools.state.error.message} />}
        {topTools.state.kind === "unavailable" && (
          <CouldNotLook
            what="top tools"
            message={topTools.state.error.message}
            onRetry={topTools.reload}
          />
        )}
        {topTools.state.kind === "notfound" && (
          <LoadFailed what="top tools" message="Route not found." />
        )}
        {(topTools.state.kind === "invalid" || topTools.state.kind === "error") && (
          <LoadFailed what="top tools" message={topTools.state.error.message} />
        )}
        {topTools.state.kind === "ready" &&
          (() => {
            const rows = metricValue(topTools.state) ?? [];
            return rows.length > 0 ? (
              <Bars
                rows={rows.map((r) => ({
                  label: r.tool,
                  value: r.credits,
                  note: `${r.credits} credits`,
                  tone: "brand" as const,
                }))}
                max={Math.max(...rows.map((r) => r.credits))}
              />
            ) : (
              <Notice title="No spend yet today">Nothing has been billed since UTC midnight.</Notice>
            );
          })()}
      </Card>

      <Card
        title="Cap overrides by tier"
        note="Tools with their own row, not inheriting the tier's * default"
        aside={<ProductTag id="mcp" />}
      >
        {overrides.state.kind === "loading" && <Loading what="cap overrides" />}
        {overrides.state.kind === "signedout" && <SignedOut />}
        {overrides.state.kind === "denied" && <Denied message={overrides.state.error.message} />}
        {overrides.state.kind === "unavailable" && (
          <CouldNotLook
            what="cap overrides"
            message={overrides.state.error.message}
            onRetry={overrides.reload}
          />
        )}
        {overrides.state.kind === "notfound" && (
          <LoadFailed what="cap overrides" message="Route not found." />
        )}
        {(overrides.state.kind === "invalid" || overrides.state.kind === "error") && (
          <LoadFailed what="cap overrides" message={overrides.state.error.message} />
        )}
        {overrides.state.kind === "ready" &&
          (() => {
            const byTier = metricValue(overrides.state) ?? {};
            const tiers = Object.keys(byTier).sort();
            return tiers.length > 0 ? (
              <Bars
                rows={tiers.map((t) => ({
                  label: t,
                  value: byTier[t] ?? 0,
                  note: "own rows",
                  tone: "brand" as const,
                }))}
                max={Math.max(...tiers.map((t) => byTier[t] ?? 0), 1)}
              />
            ) : (
              <Notice title="No overrides">Every tier resolves entirely through its * default.</Notice>
            );
          })()}
      </Card>
    </div>
  );
}

export function FleetEvidence({ config }: { config: AppConfig }) {
  return (
    <>
      <ComingSoon
        what="Fleet evidence"
        product="labeeb"
        shape="chart"
        blockers={[
          {
            label: "A warehouse query surface",
            detail:
              "The design's histograms, cost months and cohort figures come from Snowflake, not from a product API. Nothing on this gateway reaches it, and the console talks to product APIs only — never their tables.",
          },
          {
            label: "A fleet route on Labeeb's admin API",
            detail:
              "Messages, profiled users and consumption against the envelope are Labeeb's. MCP's per-metric route (GET /admin/metrics/{metric}) serves credits and admin actions for the current UTC day, which is a different question.",
          },
        ]}
        designNote="The design is explicit that when the warehouse is unavailable the cap preview cannot render and the write is blocked rather than bypassed — the evidence is a precondition for the write, not a decoration on it."
      />

      {/* Real, not blurred: MCP's half of "fleet evidence" is live, and having
          fetched it, hiding it behind the same blur as Labeeb's missing half
          would understate what this console can already show. */}
      <McpEvidence config={config} />
    </>
  );
}
